import React, { FC, useState } from "react";
import TimezoneSelect, { ITimezone } from "react-timezone-select";
import { X } from "lucide-react";
import "../../../../../styles/ambassadorChallenge/createChallengeModal.scss";
import axios from "axios";
import swal from "sweetalert";
import { useFormik } from "formik";
import * as Yup from "yup";
import { isAuth } from "../../../../../pages/auth/ProtectedRoute";
import { useAmbassadorChallengeContext } from "../../../../../contexts/AmbassadorChallengeContext";

// Validation
const validationSchema = Yup.object({
  title: Yup.string().required("Challenge title is required"),
  description: Yup.string().required("Description is required"),
  timezone: Yup.string().required("Timezone is required"),
  start: Yup.date().required("Start date and time is required"),
  end: Yup.date()
    .required("End date and time is required")
    .min(Yup.ref("start"), "End date must be later than start date"),
  min_referrals_to_qualify: Yup.number()
    .min(1, "Minimum referrals must be at least 1")
    .required("Minimum referrals are required"),
  first_prize: Yup.number()
    .min(1, "Prize amount must be at least $1")
    .required("First prize is required"),
  second_prize: Yup.number()
    .min(1, "Prize amount must be at least $1")
    .required("Second prize is required"),
  third_prize: Yup.number()
    .min(1, "Prize amount must be at least $1")
    .required("Third prize is required"),
  duration_type: Yup.string().required("Duration type is required"),
});

const CreateChallengeModal: FC<CreateChallengeModalProps> = ({ onClose }) => {
  const [submitting, setSubmitting] = useState<boolean>(false);

  const { fetchLeaderboard } = useAmbassadorChallengeContext();

  // initial values
  const formik = useFormik({
    initialValues: {
      title: "",
      description: "",
      start: "",
      end: "",
      min_referrals_to_qualify: 100,
      first_prize: 500,
      second_prize: 250,
      third_prize: 100,
      duration_type: "daily",
      timezone: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      const url = `${process.env.REACT_APP_HOST_API}ambassador/challenges`;
      const headers = {
        Authorization: `Bearer ${isAuth()}`,
      };

      const challengeData: CreateChallengePayload = {
        title: values.title,
        description: values.description,
        start: values.start,
        end: values.end,
        status: "active",
        min_referrals_to_qualify: values.min_referrals_to_qualify,
        first_prize: values.first_prize,
        second_prize: values.second_prize,
        third_prize: values.third_prize,
        duration_type: values.duration_type,
        timezone: values.timezone,
      };

      console.log('challengeData:', challengeData);

      try {
        setSubmitting(true);
        const response = await axios.post(url, challengeData, { headers });
        if (response.status === 200) {
          swal({
            title: "Success!",
            text: response.data.message,
            icon: "success",
          });
          onClose();

          fetchLeaderboard();
        }
      } catch (error) {
        const axiosError = error as AxiosErrorResponse;
        if (axiosError.response) {
          swal({
            title: "Error!",
            text: axiosError.response.data.message,
            icon: "error",
          });
        }
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <div className="overlay">
      <div className="modal">
        <div className="modal-header">
          <h3>Create New Challenge</h3>
          <button className="close-btn" onClick={onClose}>
            <X size={25} />
          </button>
        </div>
        <p className="subtext">
          Set up a new Ambassador Challenge with rewards and tracking
        </p>

        <div className="form-container">
          <form onSubmit={formik.handleSubmit}>
            <div className="field">
              <label htmlFor="title">Challenge Title</label>
              <input
                id="title"
                type="text"
                placeholder="e.g Ambassador challenge - week 42"
                {...formik.getFieldProps("title")}
              />
              {formik.touched.title && formik.errors.title ? (
                <div className="error">{formik.errors.title}</div>
              ) : null}
            </div>

            <div className="field">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                placeholder="Type here"
                {...formik.getFieldProps("description")}
              ></textarea>
              {formik.touched.description && formik.errors.description ? (
                <div className="error">{formik.errors.description}</div>
              ) : null}
            </div>

            <div className="field">
              <label htmlFor="timezone">Timezone</label>
              <TimezoneSelect
                value={formik.values.timezone}
                onChange={(value: ITimezone) => {
                  formik.setFieldValue(
                    "timezone",
                    typeof value === "string" ? value : value.value
                  );
                }}

                className="timezone-select"
              />
              {formik.touched.timezone && formik.errors.timezone ? (
                <div className="error">{formik.errors.timezone}</div>
              ) : null}
            </div>

            <div className="date-row">
              <div className="date-field">
                <label htmlFor="start">Start Date</label>
                <input
                  id="start"
                  type="datetime-local"
                  {...formik.getFieldProps("start")}
                />
                {formik.touched.start && formik.errors.start ? (
                  <div className="error">{formik.errors.start}</div>
                ) : null}
              </div>

              <div className="date-field">
                <label htmlFor="end">End Date</label>
                <input
                  id="end"
                  type="datetime-local"
                  {...formik.getFieldProps("end")}
                />
                {formik.touched.end && formik.errors.end ? (
                  <div className="error">{formik.errors.end}</div>
                ) : null}
              </div>
            </div>

            <div className="date-row">
              <div className="date-field">
                <label htmlFor="duration_type">Duration Type</label>
                <select
                  id="duration_type"
                  {...formik.getFieldProps("duration_type")}
                >
                  <option value="">-select duration-</option>
                  <option value="daily">daily</option>
                  <option value="weekly">weekly</option>
                  <option value="monthly">monthly</option>
                </select>

                {formik.touched.duration_type && formik.errors.duration_type ? (
                  <div className="error">{formik.errors.duration_type}</div>
                ) : null}
              </div>

              <div className="date-field">
                <label htmlFor="min_referrals_to_qualify">Minimum Points</label>
                <input
                  id="min_referrals_to_qualify"
                  type="number"
                  placeholder="e.g 100"
                  {...formik.getFieldProps("min_referrals_to_qualify")}
                />
                {formik.touched.min_referrals_to_qualify &&
                formik.errors.min_referrals_to_qualify ? (
                  <div className="error">
                    {formik.errors.min_referrals_to_qualify}
                  </div>
                ) : null}
              </div>
            </div>

            <div className="reward-section">
              <h4>Reward Configuration</h4>
              <div className="reward-inputs">
                <div className="reward-field">
                  <input
                    type="number"
                    placeholder="1st Place"
                    id="first_prize"
                    {...formik.getFieldProps("first_prize")}
                  />
                  {formik.touched.first_prize && formik.errors.first_prize ? (
                    <div className="error">{formik.errors.first_prize}</div>
                  ) : null}
                </div>

                <div className="reward-field">
                  <input
                    type="number"
                    placeholder="2nd Place"
                    id="second_prize"
                    {...formik.getFieldProps("second_prize")}
                  />
                  {formik.touched.second_prize && formik.errors.second_prize ? (
                    <div className="error">{formik.errors.second_prize}</div>
                  ) : null}
                </div>

                <div className="reward-field">
                  <input
                    type="number"
                    placeholder="3rd Place"
                    {...formik.getFieldProps("third_prize")}
                  />
                  {formik.touched.third_prize && formik.errors.third_prize ? (
                    <div className="error">{formik.errors.third_prize}</div>
                  ) : null}
                </div>
              </div>

              {/* <div className="toggle-container">
                <div className="toggle-labels">
                  <label htmlFor="autoDist">Auto-distribute rewards</label>
                  <small>
                    Automatically credit winners&#39; wallets when challenge ends
                  </small>
                </div>

                <div
                  className={`toggle-switch ${autoDistribute ? "active" : ""}`}
                  onClick={() => setAutoDistribute((prev) => !prev)}
                >
                  <div className="toggle-circle" />
                </div>
              </div> */}
            </div>

            <div className="modal-footer">
              <button className="draft-btn">Save as draft</button>
              {!submitting && (
                <button className="publish-btn" type="submit">
                  Publish challenge
                </button>
              )}

              {submitting && (
                <button className="loading-button">
                  <span className="spinner"></span>
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateChallengeModal;
